<template>
	<div id="student-marks">
		<!-- update scales -->
		<v-dialog v-model="dialog" max-width="400" :persistent="submitLoading">
			<v-form ref="form" :disabled="submitLoading" @submit.prevent="submit">
				<v-card>
					<v-card-title class="justify-space-between">
						<span>تعديل مقاييس {{subjectRows.find(c => c.subjectId === editSubjectId)?.text}}</span>
						<v-btn icon small @click="dialog = false">
							<v-icon>$close</v-icon>
						</v-btn>
					</v-card-title>
					<v-card-text class="pb-0">
						<v-row v-if="scales.length" dense>
							<v-col
								v-for="(scale, i) in scales"
								:key="i"
								cols="12"
								class="d-flex align-center"
							>
								<span class="font-weight-bold text-body-1 me-2">{{i + 1}}.</span>
								<v-text-field
									v-model="scale.code"
									outlined
									dense
									hide-details
									:rules="rules.required"
								></v-text-field>

								<v-btn
									tabindex="-1"
									icon
									small
									class="ms-2"
									@click="scales.splice(i, 1)"
								>
									<v-icon>$close</v-icon>
								</v-btn>
							</v-col>
						</v-row>

						<p v-else class="text-body-1 mb-3">
							إذا لم يتم إضافة مقاييس سيتم اعتماد (NI, F, G, VG, E)
						</p>

						<v-btn
							class="mt-2"
							color="primary"
							outlined
							small
							@click="scales.push({
								id: scales.length ? scales[scales.length - 1].id + 1 : 1,
								code: ''
							})"
						>
							<v-icon>$add</v-icon>
							إضافة مقياس
						</v-btn>
					</v-card-text>
					<v-card-actions class="justify-end">
						<v-btn
							depressed
							color="primary"
							type="submit"
							:loading="submitLoading"
						>
							حفظ
						</v-btn>
						<v-btn
							depressed
							:disabled="submitLoading"
							@click="dialog = false"
						>
							إلغاء
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- top row -->
		<div class="d-flex flex-wrap flex-sm-row flex-column-reverse align-sm-center">
			<!-- breadcrumbs -->
			<bee-breadcrumbs
				:items="[
					{
						to: {
							name: 'marks',
							params: {
								classId: $route.params.classId,
								courseId: $route.params.courseId,
							},
							query: { conceptId: $route.query.conceptId }
						},
						disabled: false,
						icon: 'mdi-bookshelf',
						text: 'العلامات'
					},
					{
						disabled: false,
						text: getClassById(+$route.params.classId)?.name
					},
					{
						disabled: true,
						text: 'المقاييس'
					},
				]"
				class="mt-sm-0 mt-3"
			/>
		</div>

		<v-divider class="my-3"/>
		
		<!-- table -->
		<bee-handy-table
			:headers="headers"
			:items="flattedRows"
			:loading="loading"
			dense
			zebra
			fixed-header
			:height="$vuetify.breakpoint.height - 120"
			hide-default-footer
			:items-per-page="-1"
			shown-rows-text
			with-columns-divider
			table-class="overflow-hidden"
			:data-table-props="{ disableSort: true }"
		>
			<template v-slot:item="{ item, index }">
				<tr
					:class="
						index % 2 === 1
							? getConcept(item.subjectGroupId, 'colorClass') + '-light'
							: 'bee-handy-table--zebra-color'
					"
				>
					<template v-for="(header, i) in headers">
						<!-- subjectGroupId -->
						<template v-if="header.name === 'subjectGroupId'">
							<td
								v-if="index === 0 || flattedRows[index - 1]?.subjectGroupId !== item.subjectGroupId"
								:class="[
									...getBindRowClass(header),
									getConcept(item.subjectGroupId, 'colorClass')
								]"
								:rowspan="getConcept(item.subjectGroupId, 'subRows').length"
								:key="i"
							>
								<div class="d-inline-flex pb-1 text-no-wrap rotate-header-90">
									{{item.subjectGroupName}}
								</div>
							</td>
						</template>

						<!-- actions -->
						<td
							v-else-if="header.name === 'actions'"
							:key="i"
							:class="getBindRowClass(header)"
						>
							<v-btn
								icon
								small
								color="primary"
								@click="editSubjectId = item.subjectId; dialog = true;"
							>
								<v-icon size="22">$edit</v-icon>
							</v-btn>
						</td>
						
						<!-- other headers -->
						<td
							v-else
							:key="i"
							:class="getBindRowClass(header)"
						>
							{{
								header.setValueExpr
									? header.setValueExpr(item[header.value])
									: item[header.value] || '-'
							}}
						</td>
					</template>
				</tr>
			</template>
		</bee-handy-table>
	</div>
</template>

<script>
import rules from '@/helpers/validation-rules';
import { mapGetters } from 'vuex';

export default {
	name: 'MarksScales',

	data: () => ({
		// helpers
		rules,

		loading: false,
		submitLoading: false,

		courseId: null,

		subjectRows: [],

		// dialog
		editSubjectId: null,

		dialog: false,
		scales: [],

		// data
		student: {},
	}),

	watch: {
		dialog(val) {
			if (val) {
				if (typeof this.editSubjectId === 'number') {
					const row = this.flattedRows.find(c => c.subjectId === this.editSubjectId);
					if (row) this.scales = row.scales.map((c, i) => ({ id: i + 1, code: c }));
				}
			} else {
				this.scales = [];
				this.$refs.form.resetValidation();
			}
		},
	},

	computed: {
		...mapGetters({
			getClassById: 'classes/getClassById'
		}),

		headers() {
			const headers = [
				{
					text: '',
					name: 'subjectGroupId',
					value: 'subjectGroupId',
					width: 40,
				},
				{
					text: 'المادة',
					name: 'subjectName',
					value: 'subjectName',
					width: 200,
					divider: false,
				},
				{
					text: 'المقاييس',
					name: 'scales',
					value: 'scales',
					setValueExpr: val => Array.isArray(val) && val.length ? val.join(', ') : 'NI, F, G, VG, E',
				},
				{
					text: '',
					name: 'actions',
					value: 'actions',
					width: 30
				}
			];

			for (let i = 0; i < headers.length; i++) {
				headers[i].class = headers[i].class ? headers[i].class + ' color-1' : 'color-1';
				headers[i].columnClass = headers[i].columnClass ? headers[i].columnClass + ' px-2' : 'px-2';
			}
			return headers;
		},

		nestedRows() {
			const headers = this.subjectRows;

			for (let i = 0; i < headers.length; i++) {
				headers[i].columnClass = headers[i].columnClass ? headers[i].columnClass + ' px-0' : 'px-0';
				headers[i].colorClass = `color-${((i) % 6) + 1}`;
				headers[i].align = 'center';
			}
			return headers;
		},

		flattedRows() {
			const flattedRows = this.nestedRows.flatMap(header => {
				if (header.subRows) {
					return header.subRows.map(child => ({
						subjectGroupId: header.id,
						subjectGroupName: header.text,
						...child,
					}))
				}
				return header;
			});

			// generate values
			const items = flattedRows.flatMap(row => {
				return this.headers.reduce((res, a, i, arr) => {
					if (!(a.value in res) && i !== arr.length - 1) {
						res[a.value] = row[a.value];
					}
					return { ...row, ...res };
				}, {
					subjectGroupId: row.subjectGroupId,
					subjectGroupName: row.subjectGroupName,
					subjectName: row.text,
				})
			})
			
			return items;
		},
	},

	methods: {
		getConcept(subjectGroupId, propSearch = null) {
			const concept = this.nestedRows.find(c => c.id === subjectGroupId)
			if (concept) {
				if (propSearch) return concept[propSearch] || null;
				return concept;
			}
			return {};	
		},

		getBindRowClass(header) {
			return [
				header.cellClass,
				header.columnClass,
				{ 'v-data-table__divider': header.divider },
				`text-${header.align || 'start'}`,
			]
		},

		// fetch
		fetchSubjects(isSilenceUpdate = false) {
			if (!this.$route.params.courseId) return;
			let promise;

			this.loading = !isSilenceUpdate;
			if (!this.subjectRows.length || isSilenceUpdate) {
				promise = this.$store.dispatch('subjects/fetchByClassId', {
					classId: this.$route.params.classId,
					isForceUpdate: isSilenceUpdate
				}).then(data => {
					this.subjectRows = data.map(c => ({
						id: c.id,
						text: c.name,
						subRows: c.subjectChilds.map(e => ({
							subjectId: e.id,
							text: e.name,
							scales: e.scale.map(c => c.code),
						}))
					}));
				})
			} else {
				this.subjectRows = this.subjectRows.map(c => ({
					id: c.id,
					text: c.text,
					subRows: c.subRows.map(e => ({
						text: e.text,
						subjectId: e.subjectId,
						scales: e.scales.map(c => c.code),
					}))
				}));
				promise = Promise.resolve();
			}

			return promise.finally(() => {
				this.loading = false;
			});
		},

		submit() {
			if (this.$refs.form.validate()) {
				this.submitLoading = true;
				this.$store.dispatch('subjects/updateScales', {
					subjectId: this.editSubjectId,
					scales: this.scales.map(c => c.code)
				}).then(() => {
					// local row update
					const row = this.flattedRows.find(c => c.subjectId === this.editSubjectId);
					row.scales = this.scales.map(c => c.code);
					this.dialog = false;

					// update actual data
					this.fetchSubjects(true);

					this.$snackbar.info('تم تحديث المقاييس بنجاح');
					this.dialog = false;
				}).finally(() => {
					this.submitLoading = false;
				})
			}
		}
	},

	created() {
		this.courseId = +this.$route.params.courseId || null;
		this.fetchSubjects();
	}
}
</script>

<style lang="scss">
#student-marks {
	.v-data-table-header th {
		white-space: pre-line;
	}
}
</style>